import { SyntheticEvent, useMemo } from "react";

import ExpandMore from "@mui/icons-material/ExpandMore";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import ListItem from "@mui/material/ListItem";
import TextField from "@mui/material/TextField";
import { ModelType } from "../../../API";
import { ServiceTypeEnum } from "../../../pages/model-manager/variables/modelManager";
import { AutocompleteOptionType } from "../../models/autocomplete";

interface ServiceTypeSelectProps {
  value: string | null;
  onChange: (
    event: SyntheticEvent<Element, Event>,
    value: string | null
  ) => void;
  AutocompleteProps?: Omit<
    AutocompleteProps<AutocompleteOptionType, false, false, false>,
    "renderInput" | "options"
  >;
  hasError?: boolean;
  errorMessage?: string;
}

// const options: string[] = ["fire", "smoke", "gasleak", "liquidleak", "agg"];

function ServiceTypeSelect({
  value,
  onChange,
  AutocompleteProps,
  hasError,
}: ServiceTypeSelectProps): JSX.Element {
  const props = AutocompleteProps ?? {};

  const options = useMemo((): AutocompleteOptionType[] => {
    return [
      { title: ServiceTypeEnum.FIRE, value: ModelType.fire },
      { title: ServiceTypeEnum.SMOKE, value: ModelType.smoke },
      { title: ServiceTypeEnum.GAS_LEAK, value: ModelType.gasleak },
      {
        title: ServiceTypeEnum.LIQUID_LEAK,
        value: ModelType.liquidleak,
      },
      { title: ServiceTypeEnum.GATE_GUARD, value: ModelType.agg },
      { title: ServiceTypeEnum.HARD_HAT, value: ModelType.hardhat },
      // FEATURE FLAG - PERSONNEL DETECTION. PREVENT NEW FEATURE FROM BEING ENABLED.
      // { title: ServiceTypeEnum.VEHICLE, value: ModelType.vehicle },
      // { title: ServiceTypeEnum.PEOPLE, value: ModelType.people },
    ];
  }, []);

  const selectedOption = useMemo((): AutocompleteOptionType => {
    if (!value) return null;

    return options.find(opt => opt?.value === value) ?? null;
  }, [value, options]);

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(event, newValue: AutocompleteOptionType): void =>
        onChange(
          event as unknown as SyntheticEvent<Element, Event>,
          newValue?.value ?? null
        )
      }
      popupIcon={<ExpandMore />}
      options={options}
      getOptionLabel={(option: AutocompleteOptionType): string =>
        option?.title || ""
      }
      renderInput={params => (
        <TextField
          {...params}
          error={hasError}
          label="Model"
          variant="outlined"
        />
      )}
      renderOption={(props, option) => (
        <ListItem {...props}>{option?.title}</ListItem>
      )}
      isOptionEqualToValue={(option, val) => option?.value === val?.value}
      {...props}
    />
  );
}

export default ServiceTypeSelect;
